import { GetInputSourcePropertiesResult, SceneItemClass } from '../../../../types/obs';
import { promisefy } from '../../../../utils/promisefy';

interface InputSourcePropertiesAdapter {
  platform: 'windows' | 'macos';
  audioInputSourceTypeClass: SceneItemClass;
  videoInputSourceTypeClass: SceneItemClass;
  displayCaptureSourceTypeClass: SceneItemClass;
  audioInputIdPropertyName: GetInputSourcePropertiesResult['properties'][number]['name'];
  videoInputIdPropertyName: GetInputSourcePropertiesResult['properties'][number]['name'];
  displayCaptureIdPropertyName: GetInputSourcePropertiesResult['properties'][number]['name'];

  getAudioInputSourceType: () => Promise<GetInputSourcePropertiesResult>;
  getVideoInputSourceType: () => Promise<GetInputSourcePropertiesResult>;
  getDisplayCaptureSourceType: () => Promise<GetInputSourcePropertiesResult>;
}

class WindowsInputSourcePropertiesAdapter implements InputSourcePropertiesAdapter {
  public platform = 'windows' as const;
  public audioInputSourceTypeClass = 'wasapi_input_capture' as const;
  public videoInputSourceTypeClass = 'dshow_input' as const;
  public displayCaptureSourceTypeClass = 'monitor_capture' as const;

  public audioInputIdPropertyName = 'device_id' as const;
  public videoInputIdPropertyName = 'video_device_id' as const;
  public displayCaptureIdPropertyName = 'monitor_id' as const;

  public async getAudioInputSourceType() {
    return promisefy(window.host.getInputSourceProperties)({ class: this.audioInputSourceTypeClass });
  }

  public async getVideoInputSourceType() {
    return promisefy(window.host.getInputSourceProperties)({ class: this.videoInputSourceTypeClass });
  }

  public async getDisplayCaptureSourceType() {
    return promisefy(window.host.getInputSourceProperties)({ class: this.displayCaptureSourceTypeClass });
  }
}

class MacosInputSourcePropertiesAdapter implements InputSourcePropertiesAdapter {
  public platform = 'macos' as const;
  public audioInputSourceTypeClass = 'coreaudio_input_capture' as const;
  public videoInputSourceTypeClass = 'macos-avcapture' as const;
  public displayCaptureSourceTypeClass = 'screen_capture' as const;

  public audioInputIdPropertyName = 'device_id' as const;
  public videoInputIdPropertyName = 'device' as const;
  public displayCaptureIdPropertyName = 'display_uuid' as const;

  public async getAudioInputSourceType() {
    return promisefy(window.host.getInputSourceProperties)({ class: this.audioInputSourceTypeClass });
  }

  public async getVideoInputSourceType() {
    return promisefy(window.host.getInputSourceProperties)({ class: this.videoInputSourceTypeClass });
  }

  public async getDisplayCaptureSourceType() {
    return promisefy(window.host.getInputSourceProperties)({ class: this.displayCaptureSourceTypeClass });
  }
}

export class InputSourcePropertiesClientProvider {
  static async createSourcePropertiesClient(): Promise<InputSourcePropertiesAdapter> {
    const hostProperties = await promisefy(window.host.getHostProperties)();
    const { platform } = hostProperties;

    if (platform === 'windows') {
      return new WindowsInputSourcePropertiesAdapter();
    }

    return new MacosInputSourcePropertiesAdapter();
  }
}
